// Ported from lora-scan apps/backend/src/services/videoShoot.service.ts (spreadOverTime); near-duplicate and sharpest-first picking added for FaceLock Scan.
/**
 * Choosing frames from a turn.
 *
 * A slow turn sampled a few times a second produces runs of near-identical
 * frames, and a training set full of one pose has learned one pose. So frames
 * are chosen SPREAD OVER TIME, not by face size, and near-duplicates are
 * dropped. Inside one bucket the sharpest frame wins.
 */

/**
 * Keep every Nth frame in TIME order, so the selection spans the whole turn.
 * Frames carry `index` (capture order).
 */
export function spreadOverTime(frames, want) {
  if (want <= 0 || frames.length === 0) return [];
  if (frames.length <= want) return [...frames].sort((a, b) => a.index - b.index);
  const byTime = [...frames].sort((a, b) => a.index - b.index);
  const step = byTime.length / want;
  const out = [];
  for (let i = 0; i < want; i++) out.push(byTime[Math.floor(i * step)]);
  return out;
}

/** What "too alike" means. Angles in degrees, expression 0..1, thumb 0..1 per pixel. */
export const NEAR_DUPLICATE = Object.freeze({
  /** Thumbnails this alike are the same picture whatever the numbers say. */
  sameThumb: 0.02,
  /** Otherwise alike only when the pose, the expression AND the picture barely moved. */
  pose: 3.5,
  expr: 0.1,
  thumb: 0.06,
  /** Frames of one bucket are at least this far apart in time. */
  minGapMs: 700,
});

const num = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

function diffOf(a, b) {
  const pa = [num(a.yaw), num(a.pitch), num(a.bodyYaw)];
  const pb = [num(b.yaw), num(b.pitch), num(b.bodyYaw)];
  let pose = 0;
  let poseKnown = false;
  for (let i = 0; i < 3; i++) {
    if (pa[i] === null || pb[i] === null) continue;
    poseKnown = true;
    let d = Math.abs(pa[i] - pb[i]);
    if (i === 2 && d > 180) d = 360 - d;
    pose = Math.max(pose, d);
  }
  let expr = 0;
  for (const k of ["smile", "jaw"]) {
    const x = num(a[k]);
    const y = num(b[k]);
    if (x !== null && y !== null) expr = Math.max(expr, Math.abs(x - y));
  }
  let thumb = null;
  if (Array.isArray(a.thumb) && Array.isArray(b.thumb) && a.thumb.length && a.thumb.length === b.thumb.length) {
    let s = 0;
    for (let i = 0; i < a.thumb.length; i++) s += Math.abs(a.thumb[i] - b.thumb[i]);
    thumb = s / a.thumb.length;
  }
  return { pose: poseKnown ? pose : null, expr, thumb };
}

/** How two frames differ: largest pose angle, largest expression change, mean thumbnail difference. */
export function frameDifference(a, b) {
  return diffOf(a, b);
}

export function isNearDuplicate(a, b, opts = NEAR_DUPLICATE) {
  const d = diffOf(a, b);
  if (d.thumb !== null && d.thumb < opts.sameThumb) return true;
  const poseSame = d.pose === null || d.pose < opts.pose;
  const thumbSame = d.thumb === null || d.thumb < opts.thumb;
  // With no pose and no picture to compare there is nothing to call alike.
  if (d.pose === null && d.thumb === null) return false;
  return poseSame && d.expr < opts.expr && thumbSame;
}

/**
 * The `count` best frames of one bucket: sharpest first, never two that are
 * near-duplicates, never two closer in time than minGapMs. Frames carry
 * `t` (ms) and `sharpness`. `others` are frames already chosen elsewhere that
 * a pick must not duplicate either.
 */
export function pickBest(candidates, count, opts = NEAR_DUPLICATE, others = []) {
  const sorted = [...candidates].sort((a, b) => (b.sharpness || 0) - (a.sharpness || 0));
  const out = [];
  for (const c of sorted) {
    if (out.length >= count) break;
    if (out.some((p) => Math.abs((p.t || 0) - (c.t || 0)) < opts.minGapMs)) continue;
    if (out.some((p) => isNearDuplicate(p, c, opts))) continue;
    if (others.some((p) => isNearDuplicate(p, c, opts))) continue;
    out.push(c);
  }
  return out.sort((a, b) => (a.t || 0) - (b.t || 0));
}
